import React, { useState } from 'react';
import API_URL from '../config';
import { Link } from 'react-router-dom';
import './RegistrationPage.css';

function RegistrationPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    society: '',
    position: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear society and position if job title changes
    if (name === 'jobTitle' && value !== 'society_colleague') {
      setFormData(prev => ({
        ...prev,
        society: '',
        position: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.jobTitle) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate society colleague fields
      if (formData.jobTitle === 'society_colleague') {
        if (!formData.society || !formData.position) {
          setError('Society and Position are required for Society Colleagues');
          setLoading(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setSuccess(true);

    } catch (err) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="registration-page">
        <section className="success-section">
          <div className="container">
            <div className="success-card">
              <div className="success-icon">✓</div>
              <h1>Registration Successful!</h1>
              <p>Thank you for registering for CISAC General Assembly 2026.</p>
              <p>A confirmation email has been sent to <strong>{formData.email}</strong></p>

              <div className="next-steps">
                <h3>Next Steps</h3>
                <ol>
                  <li>Check your email for confirmation</li>
                  <li>Book your accommodation</li>
                  <li>Review the event schedule</li>
                </ol>
              </div>

              <div className="success-actions">
                <Link to="/accommodation" className="btn btn-primary">
                  Book Accommodation
                </Link>
                <Link to="/schedule" className="btn btn-secondary">
                  View Schedule
                </Link>
                <Link to="/" className="btn btn-secondary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="registration-page">
      <section className="registration-hero">
        <div className="container">
          <h1>REGISTRATION</h1>
          <p>Register for the CISAC General Assembly 2026 in Paris</p>
        </div>
      </section>

      <section className="section registration-form-section">
        <div className="container">
          <form onSubmit={handleSubmit} className="registration-form">
            <h3>Registration Form</h3>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="jobTitle">
                Job Title <span className="required">*</span>
              </label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="jobTitle"
                    value="society_colleague"
                    checked={formData.jobTitle === 'society_colleague'}
                    onChange={handleChange}
                    required
                  />
                  <span>Society Colleague</span>
                </label>

                <label className="radio-option">
                  <input
                    type="radio"
                    name="jobTitle"
                    value="creator"
                    checked={formData.jobTitle === 'creator'}
                    onChange={handleChange}
                    required
                  />
                  <span>Creator</span>
                </label>

                <label className="radio-option">
                  <input
                    type="radio"
                    name="jobTitle"
                    value="other"
                    checked={formData.jobTitle === 'other'}
                    onChange={handleChange}
                    required
                  />
                  <span>Other</span>
                </label>
              </div>
            </div>

            {formData.jobTitle === 'society_colleague' && (
              <>
                <div className="form-group">
                  <label htmlFor="society">
                    Society <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="society"
                    name="society"
                    value={formData.society}
                    onChange={handleChange}
                    placeholder="Enter your society name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="position">
                    Position <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    placeholder="Enter your position"
                    required
                  />
                </div>
              </>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Register Now'}
              </button>
            </div>

            <p className="form-note">
              <span className="required">*</span> Required fields
            </p>
          </form>
        </div>
      </section>

      <section className="section contact-section-reg">
        <div className="container">
          <h3>Questions?</h3>
          <p>If you have any questions about registration, please contact:</p>

          <div className="contact-box">
            <p>Registration & Accommodation</p>
            <p>Email: <a href="mailto:2026GA@CISAC.org">2026GA@CISAC.org</a></p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default RegistrationPage;
